import React, { useEffect, useState } from 'react';
import { TenantReview } from '../../../core/types/portal';
import { reviewService } from '../../../core/services/firebase/reviewService';
import { StarRating } from './StarRating';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { ThumbsUp, MessageSquare, ShieldCheck, User } from 'lucide-react';
import { QueryDocumentSnapshot } from 'firebase/firestore';

interface ReviewListProps {
  tenantId: string;
}

export const ReviewList: React.FC<ReviewListProps> = ({ tenantId }) => {
  const [reviews, setReviews] = useState<TenantReview[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot | null>(null);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadInitialReviews();
  }, [tenantId]);

  const loadInitialReviews = async () => {
    setLoading(true);
    try {
      const { reviews: data, lastVisible: last } = await reviewService.getTenantReviews(tenantId);
      setReviews(data);
      setLastVisible(last);
      setHasMore(data.length === 10);
    } catch (error) {
      console.error('Error loading reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    if (!lastVisible || !hasMore) return;
    
    try {
      const { reviews: data, lastVisible: last } = await reviewService.getTenantReviews(tenantId, 10, lastVisible);
      setReviews(prev => [...prev, ...data]);
      setLastVisible(last);
      setHasMore(data.length === 10);
    } catch (error) {
      console.error('Error loading more reviews:', error);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="animate-pulse flex gap-4 bg-white p-6 rounded-xl border">
            <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/4"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
        <div className="text-5xl mb-4 opacity-50">⭐</div>
        <h3 className="text-xl font-bold text-gray-800 mb-2">Ainda não há avaliações</h3>
        <p className="text-gray-600">Seja o primeiro a avaliar esta empresa!</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-800">
          Avaliações ({reviews.length}{hasMore ? '+' : ''})
        </h3>
      </div>

      <div className="grid gap-6">
        {reviews.map((review) => (
          <div key={review.id} className="bg-white p-6 rounded-xl border shadow-sm hover:shadow-md transition-shadow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden border border-gray-200">
                  {review.userPhotoURL ? (
                    <img src={review.userPhotoURL} alt={review.userName} className="w-full h-full object-cover" />
                  ) : (
                    <User size={24} className="text-gray-400" />
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-gray-900">{review.userName}</span>
                    {review.isVerified && (
                      <span className="flex items-center gap-0.5 text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-full font-bold uppercase tracking-wider">
                        <ShieldCheck size={10} />
                        Verificado
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {format(new Date(review.createdAt), "dd 'de' MMMM, yyyy", { locale: ptBR })}
                  </span>
                </div>
              </div>
              <StarRating rating={review.rating} size={16} />
            </div>

            {review.title && <h4 className="font-bold text-gray-800 mb-2">{review.title}</h4>}
            
            <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
              {review.comment}
            </p>

            {review.photos && review.photos.length > 0 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {review.photos.map((photo, idx) => (
                  <img 
                    key={idx} 
                    src={photo} 
                    className="w-20 h-20 object-cover rounded-lg border shadow-sm cursor-pointer hover:opacity-80 transition-opacity" 
                    alt="Foto da avaliação"
                  />
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-gray-50">
              <button 
                onClick={() => reviewService.markAsHelpful(review.id)}
                className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-blue-600 transition-colors"
              >
                <ThumbsUp size={14} />
                Útil ({review.helpfulCount})
              </button>
              
              <button className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-gray-700 transition-colors">
                <MessageSquare size={14} />
                Denunciar
              </button>
            </div>

            {review.response && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border-l-4 border-brand-orange">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-bold text-gray-800 text-sm">Resposta da Empresa</span>
                  <span className="text-[10px] text-gray-500">
                    {format(new Date(review.response.date), "dd/MM/yyyy")}
                  </span>
                </div>
                <p className="text-gray-700 text-sm italic">
                  "{review.response.text}"
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {hasMore && (
        <button
          onClick={loadMore}
          className="w-full py-4 text-brand-blue font-bold hover:bg-gray-50 rounded-xl transition-colors"
        >
          Carregar mais avaliações
        </button>
      )}
    </div>
  );
};
